/* eslint-disable react/display-name */
import { AbstractDestroyable, Burden } from '@deqo/foundation';
import { Container, injectable, interfaces } from 'inversify';
import React from 'react';
import { CommandDispatcher, createBoundCommandDispatcher } from '../concepts/commands/CommandDispatcher';
import { CommandRegistry } from '../concepts/commands/CommandRegistry';
import { ResourceManager } from '../concepts/resources/ResourceManager';
import { AudioManager } from '../platform/AudioManager';
import { ClipboardFactory, ClipboardService } from '../platform/Clipboard';
import { Monitor } from '../platform/Monitor';
import { LocalStorageService, SessionStorageService, StorageService } from '../platform/StorageService';
import { LoadingFrame } from '../ui/LoadingGuard';
import { AppEvents } from './AppEvents';
import { AppLocation } from './AppLocation';
import { AppNavigator, NavigatorDriver } from './AppNavigator';
import { Activator, ContainerScope, Resolver, Scheduler } from './Container';
import { Crash } from './Crash';
import { ShortcutRegistry } from './ShortcutRegistry';
import { ShortcutSupport } from './ShortcutSupport';
import { Wrapper, WrapperMount } from './Wrapper';
import { ModelBroker } from './dashboard/Models';
import { PopupManager } from './popups/PopupManager';
import { PopupSupport } from './popups/PopupSupport';

const shallowCompare = (a: unknown, b: unknown) =>
    Object.keys(a).length === Object.keys(b).length &&
    Object.keys(a).every(key => a[key] === b[key]);

@injectable()
class Garbage extends AbstractDestroyable {
    constructor(burdens: Burden | Burden[]) {
        super();
        this.burden(...(Array.isArray(burdens) ? burdens : [burdens]));
    }
}

type RefreshResult = void | Burden | Burden[];

export interface ScopeStrategy<P> {
    traceAs?: string;
    shouldRefresh?(props: P, container: Container): boolean;
    refresh(props: P, container: Container, progress: (n: number) => void): RefreshResult | Promise<RefreshResult>;
}

const depthOf = (container: interfaces.Container) => {
    return container.parent ? 1 + depthOf(container.parent) : 0;
};

type ScopeProps = {
    loader?: React.ComponentType<{ progress: number | false }>,
    crash?: React.ComponentType<{ error: Error }>,
    children?: React.ReactNode,
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const createScope = <P extends unknown = Record<string, unknown>>(strategy: ScopeStrategy<P>) => {
    return class extends React.Component<P & ScopeProps> {

        public static contextType = ContainerScope;

        public state = {
            depth: null as number,
            error: null as Error,
            loading: true,
            progress: false as number | false,
            container: null as Container,
        };

        public render() {
            const { error, loading, container, progress } = this.state;
            const { loader, crash, children } = this.props;
            if (loading && loader) return React.createElement(loader, { progress });
            if (loading && !loader) return <LoadingFrame />;
            if (error && crash) return React.createElement(crash, { error });
            if (error && !crash) return <Crash error={error} />;
            if (!container) return null;
            const depth = depthOf(container);
            return <ContainerScope.Provider key={container.id} value={container}>
                <PopupSupport />
                <ShortcutSupport />
                {depth === 0 ? <WrapperMount>{children}</WrapperMount> : children}
            </ContainerScope.Provider>;
        }

        public async componentDidMount() {
            await this.doLoad();
        }

        public async componentDidUpdate(oldProps: P) {
            if (!shallowCompare(this.props, oldProps) && this.shouldLoad()) {
                this.doUnload();
                await this.doLoad();
            }
        }

        public shouldComponentUpdate(nextProps: any, nextState: any) {
            if (!shallowCompare(this.props, nextProps)) return true;
            if (!shallowCompare(this.state, nextState)) return true;
            return false;
        }

        public componentWillUnmount() {
            this.doUnload();
        }

        private shouldLoad() {
            if (this.state.loading) return false;
            if (this.state.error) return true;
            if (!this.state.container) return true;
            if (!strategy.shouldRefresh) return false;
            return strategy.shouldRefresh(this.props, this.state.container);
        }

        private async doLoad() {
            // const trace = strategy.traceAs ? Sentry.trace(`${strategy.traceAs}.load`) : null;
            try {
                this.setState({ loading: true, container: null, progress: false });
                const container = this.createContainer();
                const burdens = await this.invokeRefresh(container);
                container.bind(Garbage).toConstantValue(new Garbage(burdens));
                this.setState({ loading: false, error: null, container });
            }
            catch (err) {
                this.setState({ loading: false, error: err });
                Monitor.error('ScopeLoadError', err);
            }
            finally {
                // if (trace) {
                //     trace.finish();
                // }
            }
        }

        private doUnload() {
            if (this.state.container) {
                const garbage = this.state.container.get(Garbage);
                garbage.destroy();
            }
        }

        private createContainer() {
            const parent = this.context as Container;
            const container = parent?.createChild({ skipBaseClassChecks: true }) ?? this.createRootContainer();
            // Layered services available at each scope:
            container.bind(CommandRegistry).toConstantValue(new CommandRegistry(parent?.get(CommandRegistry)));
            container.bind(ShortcutRegistry).toConstantValue(new ShortcutRegistry(parent?.get(ShortcutRegistry)));
            container.bind(ModelBroker).toConstantValue(new ModelBroker(parent?.get(ModelBroker)));
            container.bind(Activator).toConstantValue(Activator.createFor(container));
            container.bind(Resolver).toConstantValue(Resolver.createFor(container));
            container.bind(CommandDispatcher).toConstantValue(createBoundCommandDispatcher(container));
            container.bind(PopupManager).toSelf().inSingletonScope();
            container.bind(Scheduler).toSelf().inSingletonScope();
            return container;
        }

        private createRootContainer() {
            const container = new Container({ skipBaseClassChecks: true });
            // Top level services only available from root:
            container.bind(AppNavigator).to(NavigatorDriver).inSingletonScope();
            container.bind(AppEvents).toSelf().inSingletonScope();
            container.bind(AppLocation).to(AppLocation).inSingletonScope();
            container.bind(AudioManager).toSelf().inSingletonScope();
            container.bind(ClipboardService).toConstantValue(ClipboardFactory.createForNavigator());
            container.bind(LocalStorageService).toConstantValue(StorageService.createLocal());
            container.bind(ResourceManager).to(ResourceManager).inSingletonScope();
            container.bind(SessionStorageService).toConstantValue(StorageService.createSession());
            container.bind(Wrapper).toSelf().inSingletonScope();
            return container;
        }

        private async invokeRefresh(container: Container): Promise<Burden[]> {
            const onProgress = (n: number) => this.setState({ progress: n });
            const toBurdens = (result: RefreshResult) => !!result ? (Array.isArray(result) ? result : [result]) : [];
            const reply = strategy.refresh(this.props, container, onProgress);
            if (reply instanceof Promise || typeof (reply) === 'object' && 'then' in reply) {
                return toBurdens(await reply);
            }
            return toBurdens(reply);
        }
    };
};
